import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from "@mui/material";
import {
  Add,
  CheckCircle,
  Cancel,
  Delete,
  VerifiedUser,
} from "@mui/icons-material";
import { feedbackAPI } from "../../services/api";

const FeedbackChannelSettings = ({ configurationId }) => {
  const [channels, setChannels] = useState([]);
  const [channelType, setChannelType] = useState("teams");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [channelName, setChannelName] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadChannels = useCallback(async () => {
    if (!configurationId) {
      setChannels([]);
      return;
    }
    try {
      setError("");
      const configId =
        typeof configurationId === "string"
          ? parseInt(configurationId, 10)
          : configurationId;
      const response = await feedbackAPI.getChannels(configId);
      setChannels(response.data || []);
    } catch (err) {
      console.error("Failed to load feedback channels:", err);
      setError(
        err.response?.data?.error || "Failed to load feedback channels.",
      );
      setChannels([]);
    }
  }, [configurationId]);

  useEffect(() => {
    setError("");
    setSuccess("");
    setChannels([]);

    if (configurationId) {
      loadChannels();
    } else {
      setChannels([]);
    }
  }, [configurationId, loadChannels]);

  const handleVerify = async () => {
    if (!webhookUrl.trim()) {
      setError("Please enter a webhook URL");
      return;
    }

    setVerifying(true);
    setError("");
    setSuccess("");

    try {
      const response = await feedbackAPI.verifyChannel({
        webhook_url: webhookUrl.trim(),
        channel_type: channelType,
      });

      if (response.data.success) {
        setSuccess("Connection verified successfully!");
      } else {
        setError(response.data.error || "Verification failed");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to verify connection");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!configurationId) {
      setError("Please select a configuration first");
      return;
    }

    if (!webhookUrl.trim()) {
      setError("Webhook URL is required");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await feedbackAPI.createChannel({
        configuration_id: configurationId,
        channel_type: channelType,
        webhook_url: webhookUrl.trim(),
        channel_name: channelName.trim() || null,
      });
      setSuccess("Feedback channel configured and verified successfully!");
      setWebhookUrl("");
      setChannelName("");
      await loadChannels();
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to configure feedback channel",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this feedback channel?")
    ) {
      return;
    }

    try {
      await feedbackAPI.deleteChannel(id);
      setSuccess("Feedback channel deleted successfully");
      await loadChannels();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to delete feedback channel",
      );
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Feedback Channels
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Configure MS Teams or Slack channels to receive user feedback
          notifications. The webhook URL will be verified before saving.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal">
          <InputLabel>Channel Type</InputLabel>
          <Select
            value={channelType}
            onChange={(e) => setChannelType(e.target.value)}
            label="Channel Type"
          >
            <MenuItem value="teams">Microsoft Teams</MenuItem>
            <MenuItem value="slack">Slack</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Webhook URL"
          variant="outlined"
          margin="normal"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          required
          placeholder="https://..."
          helperText={`Enter the ${channelType === "teams" ? "Teams" : "Slack"} webhook URL`}
        />

        <TextField
          fullWidth
          label="Channel Name (Optional)"
          variant="outlined"
          margin="normal"
          value={channelName}
          onChange={(e) => setChannelName(e.target.value)}
          placeholder="e.g., feedback-channel"
          helperText="Optional: A friendly name for this channel"
        />

        <Box display="flex" gap={2} mt={2}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleVerify}
            disabled={loading || verifying || !webhookUrl.trim()}
            startIcon={verifying ? <VerifiedUser /> : null}
          >
            {verifying ? "Verifying..." : "Verify Connection"}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Add />}
            disabled={loading || verifying}
          >
            {loading ? "Saving..." : "Save Channel"}
          </Button>
        </Box>
      </Box>

      {channels.length > 0 && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Channel Name</TableCell>
                <TableCell>Webhook URL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Verified At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.map((channel) => (
                <TableRow key={channel.id}>
                  <TableCell>
                    {channel.channel_type === "teams"
                      ? "Microsoft Teams"
                      : "Slack"}
                  </TableCell>
                  <TableCell>{channel.channel_name || "-"}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {channel.webhook_url}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {channel.is_verified ? (
                      <Chip
                        icon={<CheckCircle />}
                        label="Verified"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<Cancel />}
                        label="Not Verified"
                        color="error"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {channel.verified_at
                      ? new Date(channel.verified_at).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(channel.id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default FeedbackChannelSettings;
